/**
 * H 域提醒中心**接口契约** —— `GET /api/reminders`（站内兜底通道）+ 已读 / 已处理
 *
 * 依据：H 域 PRD **§5.1**（规则引擎）· **§5.1.4**（三档分组）· **§5.2.2**（站内可用性铁律）·
 * **§5.3**（偏好只读消费）· **§7**（验收）· `DR-038`（状态载体）· 架构 **§6.1**（契约唯一源）·
 * **§8.1**（每日 08:30 集中生成）。
 *
 * ## 🔴 本文件**不含任何口径** —— 只描述"下发形状"
 *
 * | 数字 / 判定 | 唯一实现 |
 * |---|---|
 * | 状态（`unread`/`read`/`done`/`dismissed`） | `nucleus/reminder.ts` 的 `reminderStateOf()` |
 * | 三档分组（`action`/`info`/`earlier`） | 同文件的 `reminderGroupOf()` |
 * | 关键 / 常规（`tag`） | 同文件的 `reminderIsKey()` |
 * | 该不该推（真机推送 P1 用） | 同文件的 `shouldPushReminder()` |
 * | **偏好本身**（三档 / 五类开关 / 免打扰） | 🔴 **N 域 `user_preferences`** —— 本接口**只读下发、无写入口**（`DR-031` / `DR-020`） |
 *
 * ## 🔴 两个"必须能区分"的诚实性字段
 *
 * | 字段 | 含义 | 为什么不能省 |
 * |---|---|---|
 * | `channels.wechat` | 微信订阅消息**本批未接** ⇒ 恒 `false` | 若省略，端侧无法区分"通道没接"与"已接但没授权"（`AC-H2.4` 的对象） |
 * | `earlierTruncated` | 「更早」档被 `ReminderPolicy.EarlierLimit` **截断** | 截断**必须上报** —— 静默截断 = 让用户以为历史就这么多（同 `SchedulerPolicy.MaxCatchUpDays` 的纪律） |
 *
 * ## 🔴 为什么「动作文案」不在本契约里
 *
 * 本契约只下发 **`actionable: boolean` + 结构化字段**（`type` / `variant` / `subject` /
 * `tailNumber` / `dueDate` / `daysUntil` / `amount`）。**中文文案（标题 / 描述 / 按钮）的唯一源
 * 在端侧 `features/reminder-labels.ts`** —— 与服务端只出数字、端侧出文案的既有分工一致
 * （同 K 域报表：`contract/report.ts` 出结构、`features/report-labels.ts` 出文案）。
 */

import { z } from 'zod';
import type { Cents } from '../types/brand.js';
import type {
  ReminderGroup,
  ReminderLevel,
  ReminderState,
  ReminderType,
  ReminderVariant,
} from '../enums/plan.js';

// ───────────────────────────── 查询 ─────────────────────────────

/**
 * `GET /api/reminders` 的查询契约。
 *
 * 🔴 **只控制"历史档要不要"** —— 三档分组（需要处理 / 知道就好 / **更早**）由服务端**固定**给出，
 * 不由调用方拼（拼错 = 两处判定，铁律 24）。
 */
export const ReminderQuerySchema = z.object({
  /**
   * 是否包含「更早」档（已读 / 已完成）。
   * 缺省 `true`（原型默认展示）· 上限 `ReminderPolicy.EarlierLimit` · 截断时 `earlierTruncated=true`。
   *
   * 🔴 **不能用 `z.coerce.boolean()`** —— 查询参数**永远是字符串**，
   * 而 `coerce.boolean()` 按 JS 真值转换 ⇒ **`'false'` 是 truthy ⇒ 变成 `true`**，
   * 「不显示历史」这个开关会**静默失效**（本批实测踩到：`?includeEarlier=false` 仍返回历史档）。
   * ⇒ 只接受字面量 `'true'` / `'false'` 再显式映射（**非法值当场 400，不猜**）。
   */
  includeEarlier: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .transform((v) => v === true || v === 'true')
    .optional(),
});
export type ReminderQuery = z.infer<typeof ReminderQuerySchema>;

// ───────────────────────────── 列表 ─────────────────────────────

/** 一条提醒（**合并后的展示单元** —— 合并时 `mergedCount > 1`） */
export interface ReminderView {
  /** 代表 id（合并时 = 组内第一条的 id） */
  id: string;
  /**
   * 被合并进本条的**全部原 id**（含代表自身）。
   * 🔴 「标记已读」按它逐条落库 —— 合并只是**展示**，**不改变幂等行数**（`AC-H1.9`）。
   */
  mergedIds: string[];
  type: ReminderType;
  variant: ReminderVariant;
  group: ReminderGroup;
  state: ReminderState;
  /** 关键 / 常规（服务端按 `reminderIsKey()` 算 —— 端侧只据此上色，**不自判**） */
  tag: 'key' | 'normal';
  /**
   * 触发主体名（账户名 / 预算分类名 / 目标名）—— **纯数据**，
   * 文案拼接（「招行信用卡账单已出」）归端侧 `features/reminder-labels.ts`。
   */
  subject: string;
  /** 卡尾号（还款 / 账单类才有，其余 `null`） */
  tailNumber: string | null;
  /** 到期日 / 账单日（`YYYY-MM-DD` · **账本本地日历日**） */
  dueDate: string | null;
  /** 距到期天数（`0` = 今天 · **负数 = 已逾期** · 其余 `null`） */
  daysUntil: number | null;
  /**
   * 金额（**本币分**）。
   * 🔴 `null` = **本类无金额**（如"查漏补记 N 笔"）—— **不是 `0`**（`Cents` 与 `null` 语义不同）。
   */
  amount: Cents | null;
  /** 合并后的条数（`≥1`；`>1` 时端侧按「N 张卡…合计 ¥X」呈现，`AC-H1.9`） */
  mergedCount: number;
  /**
   * 🔴 是否有**可执行动作**（`AC-H1.11`：每条要么给动作、要么明确「无需处理」）。
   * `false` ⇒ 端侧**必须**显示「无需处理」类文案（**不许什么都不显示**）。
   */
  actionable: boolean;
  /** 计划触发时刻（UTC 毫秒）—— 列表排序用 */
  triggerAt: number;
}

/**
 * 三档分组的小结（端侧按**固定顺序**渲染三个锚点）。
 *
 * 🔴 **只有 `key` 与 `count`，没有 `label`** —— 组名（「需要你处理 / 知道就好 / 更早」）
 * 是**文案**，唯一源在端侧 `features/reminder-labels.ts`（同 `type` 的显示名）。
 */
export interface ReminderGroupSummaryView {
  key: ReminderGroup;
  /** 组内条数（**合并后的展示条数**） */
  count: number;
}

/** 免打扰状态（**只读展示** —— 写入口在 N 域设置页） */
export interface ReminderDndView {
  enabled: boolean;
  /** `HH:mm`（账本时区本地时刻） */
  start: string;
  end: string;
  /** 服务端按**账本时区 + 当前时刻**算好：现在是否处于免打扰中 */
  active: boolean;
}

/** 触达通道可用性（**只读**） */
export interface ReminderChannelsView {
  /** 🔴 站内提醒中心**不依赖任何授权**、始终可用（H 域 §5.2.2 铁律）—— 恒 `true` */
  inApp: boolean;
  /** 微信订阅消息 —— ⚠️ **本批（`56`）未接**，恒 `false`（诚实下发，不假装可用） */
  wechat: boolean;
}

/** `GET /api/reminders` 响应 */
export interface ReminderListResponse {
  /** 本币（金额展示用） */
  baseCurrency: string;
  /** 账本本地今天（`YYYY-MM-DD`） */
  today: string;
  /** 当前强度档位（**只读** —— 写偏好归 N 域） */
  level: ReminderLevel;
  /** 三档分组小结（固定三组，可能 `count: 0`） */
  groups: ReminderGroupSummaryView[];
  items: ReminderView[];
  /** 未读条数（`state === 'unread'`） */
  unreadCount: number;
  /** 关键类条数 */
  keyCount: number;
  /** 常规类条数 */
  normalCount: number;
  dnd: ReminderDndView;
  channels: ReminderChannelsView;
  /** 🔴 「更早」档是否被 `EarlierLimit` **截断**（截断必须上报，不静默） */
  earlierTruncated: boolean;
}

// ─────────────────────────── 已读 / 已处理 ───────────────────────────

/**
 * `POST /api/reminders/read` 请求体。
 *
 * 🔴 `ids` 与 `all` **互斥**：两者同时给出 ⇒ `400`（**不猜意图** —— 见 `DR-036` 同族纪律：
 * "写错不报错"会让"全部标记已读"被一次误操作做掉，而它**不可撤销**）。
 * 空 `ids` 且无 `all` ⇒ **无操作**（`marked: 0`）—— 🔴 **空数组不等于"全部"**。
 */
export const ReminderReadRequestSchema = z.object({
  ids: z.array(z.string().min(1)).max(500).optional(),
  all: z.boolean().optional(),
});
export type ReminderReadRequest = z.infer<typeof ReminderReadRequestSchema>;

/** 已读 / 已处理的结果（**逐条上报实际生效数** —— `AC-C1.21` 禁静默失败的同族纪律） */
export interface ReminderMarkResultView {
  /** 由 `unread` 转为 `read`（或 `done`）的条数 */
  marked: number;
  /**
   * **未生效**的条数（已是 `read`/`done`/`dismissed`，或 id 不属于本账本）。
   * 🔴 **必须与 `marked` 分开报** —— 「没查」和「查了没事」必须长得不一样。
   */
  skipped: number;
}

/**
 * `POST /api/reminders/:id/done` 的结果。
 *
 * 语义：执行了动作（如「已还，别再提」）⇒ 写 `doneAt`。🔴 **不删行**（`§6.1 E5`：关闭 ≠ 删除历史）。
 */
export interface ReminderDoneResultView {
  id: string;
  state: ReminderState;
}
